// =============================================================
// Lions Network — frontend
//
// Bez frameworku. Tři drobnosti, které stránka potřebuje:
// podložení hlavičky po odrolování, mobilní menu a jedno
// odhalení obsahu při scrollu. Posuvník referencí (Swiper)
// je samostatný bundl, dotahuje se jen tam, kde je potřeba.
// =============================================================

import { initCookieConsent } from './cookieConsent.js';

document.documentElement.classList.add('js');

initCookieConsent();

// ── Hlavička ─────────────────────────────────────────────────

function initStickyHeader() {
    const header = document.querySelector('.ln-header');
    if (!header) return;

    const onScroll = () => header.classList.toggle('is-stuck', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
}

// bV24: zajížděcí hlavička (PC i mobil). Při scrollu dolů se schová, kousek nahoru
// (UP_DELTA px) ji zase vysune. U horního okraje stránky, s otevřeným menu nebo
// s fokusem uvnitř (klávesnice) zůstává vidět.
function initHeaderAutoHide() {
    const header = document.querySelector('.bv-header');
    if (!header) return;
    const UP_DELTA = 10;
    let lastY = window.scrollY, upFrom = lastY, ticking = false;

    const pinned = () => header.contains(document.activeElement)
        || document.querySelector('[data-nav-open][aria-expanded="true"]');

    const update = () => {
        ticking = false;
        const y = Math.max(0, window.scrollY);   // iOS „gumový" přejezd nad začátek
        if (y <= header.offsetHeight || pinned()) {
            header.classList.remove('is-hidden');
        } else if (y > lastY) {
            header.classList.add('is-hidden');
            upFrom = y;
        } else if (y < lastY && upFrom - y >= UP_DELTA) {
            header.classList.remove('is-hidden');
        }
        if (y > upFrom) upFrom = y;
        lastY = y;
    };
    window.addEventListener('scroll', () => {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    header.addEventListener('focusin', () => header.classList.remove('is-hidden'));
}

// ── Mobilní menu ─────────────────────────────────────────────

function initMobileNav() {
    // bV24: menu se rozbalí pod hlavičkou (ne dialog přes obrazovku) —
    // přepínač s aria-expanded, Escape a klik na odkaz ho zavřou.
    const nav    = document.getElementById('bvMobileNav');
    const opener = document.querySelector('[data-nav-open]');
    if (!nav || !opener) return;
    const icon = opener.querySelector('i');

    function setOpen(open) {
        nav.hidden = !open;
        // Panel překrývá stránku — obsah pod ním vyřadit z tabulátoru (fokus neuteče pod zelenou plochu).
        document.querySelectorAll('main, footer').forEach(el => el.toggleAttribute('inert', open));
        opener.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (icon) { icon.classList.toggle('fa-bars', !open); icon.classList.toggle('fa-xmark', open); }
    }

    opener.addEventListener('click', (e) => {
        const open = nav.hidden;
        setOpen(open);
        // Fokus do menu jen z klávesnice (e.detail === 0) — po kliknutí myší
        // by na první položce zbytečně svítil rámeček.
        if (open && e.detail === 0) nav.querySelector('a')?.focus();
    });
    nav.querySelectorAll('a').forEach(a => a.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !nav.hidden) { setOpen(false); opener.focus(); }
    });
    // Při přechodu na desktop menu schovat, ať po zmenšení okna nevisí otevřené.
    window.matchMedia('(min-width: 1200px)').addEventListener('change', (e) => { if (e.matches) setOpen(false); });
}

// ── Poptávkový formulář ──────────────────────────────────────
//
// Odesílá se přes FormData, ne jako JSON — jinak by neprošly přílohy.
// Odpověď se čte benevolentně: v dev režimu umí PHP warning rozbít JSON
// a formulář by pak mlčel, i když lead ve skutečnosti dorazil.

function initLeadForm() {
    const form = document.getElementById('lnLeadForm');
    if (!form) return;

    const btn = form.querySelector('button[type=submit]');
    const msg = form.querySelector('.ln-form__msg');

    // Skript reCAPTCHY (~450 kB i s fonty) se stahuje až ve chvíli, kdy
    // s formulářem někdo začne pracovat — na načtení stránky držel
    // hlavní vlákno a v Lighthouse stál body za výkon. Pro skóre v3 mu
    // zbývá vyplňování formuláře, a to je chování, na kterém záleží.
    if (form.dataset.recaptchaKey) {
        const warm = () => loadRecaptcha(form.dataset.recaptchaKey).catch(() => {});
        ['pointerover', 'focusin', 'touchstart'].forEach(ev => form.addEventListener(ev, warm, { once: true, passive: true }));
    }

    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        if (!form.reportValidity()) return;

        btn.disabled = true;
        msg.textContent = '';
        msg.className = 'ln-form__msg';

        // Token reCAPTCHY platí jen dvě minuty, takže se bere až teď —
        // ne při načtení stránky, kde by stihl vypršet.
        await fillRecaptchaToken(form);

        try {
            const res = await fetch(form.action, {
                method: 'POST',
                body: new FormData(form),
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            let data = {};
            try { data = await res.json(); } catch { /* viz poznámka výš */ }

            if (res.ok && data.ok) {
                form.reset();
                msg.textContent = 'Děkujeme, ozveme se vám co nejdřív.';
                msg.classList.add('is-ok');
            } else {
                msg.textContent = data.error || 'Odeslání se nepovedlo. Zkuste to prosím znovu.';
                msg.classList.add('is-err');
            }
        } catch {
            msg.textContent = 'Odeslání se nepovedlo. Zkuste to prosím znovu.';
            msg.classList.add('is-err');
        }

        btn.disabled = false;
    });
}

/**
 * Doplní token reCAPTCHY do skrytého pole formuláře.
 *
 * Když ochrana není zapnutá, formulář data-atributy nemá a funkce se
 * hned vrací. Selhání se jen zaloguje a odeslání pokračuje — server si
 * token stejně ověřuje sám a je lepší poslat poptávku bez tokenu než
 * ji kvůli výpadku cizího skriptu ztratit.
 */
async function fillRecaptchaToken(form) {
    const key = form.dataset.recaptchaKey;
    const field = form.querySelector('input[name="g-recaptcha-response"]');
    if (!key || !field) return;

    try {
        await loadRecaptcha(key);
        await new Promise(resolve => window.grecaptcha.ready(resolve));
        field.value = await window.grecaptcha.execute(key, { action: form.dataset.recaptchaAction || 'submit' });
    } catch (err) {
        console.warn('[reCAPTCHA] token se nepodařilo získat', err);
    }
}

let recaptchaLoading = null;

/** Načte api.js reCAPTCHY jednou za stránku; po 10 s to vzdá (odeslání pak jde bez tokenu). */
function loadRecaptcha(key) {
    if (window.grecaptcha) return Promise.resolve();
    if (recaptchaLoading) return recaptchaLoading;

    recaptchaLoading = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(key);
        s.async = true;
        const timer = setTimeout(() => reject(new Error('timeout')), 10000);
        s.onload  = () => { clearTimeout(timer); resolve(); };
        s.onerror = () => { clearTimeout(timer); reject(new Error('load failed')); };
        document.head.appendChild(s);
    });
    // Po selhání zkusit znovu při dalším pokusu, ne navždy vrátit odmítnutý slib.
    recaptchaLoading.catch(() => { recaptchaLoading = null; });
    return recaptchaLoading;
}

// ── Odhalení obsahu ──────────────────────────────────────────
//
// IntersectionObserver místo knihovny na animace — jedno gesto
// nestojí za další balík v bundlu. Bez podpory (nebo s vypnutým
// pohybem) se obsah jen zobrazí.
//
// Odhaluje se vždy celý blok, ne jednotlivé karty. Po kartách to
// nechávalo mřížku dlouho poloprázdnou a na pomalém scrollu z toho
// byla šedá díra místo obsahu.

function initReveal() {
    const items = document.querySelectorAll('.ln-reveal');
    if (!items.length) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
        items.forEach(el => el.classList.add('is-visible'));
        return;
    }

    const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
        });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });

    items.forEach(el => io.observe(el));
}

// ── Široké tabulky v textu ───────────────────────────────────
//
// Na úzkém displeji se tabulka v textu (.ln-prose) posouvá do strany
// (_page.scss). Posuvná oblast musí jít ovládat i klávesnicí (WCAG
// 2.1.1) — dostane tabindex, a to jen když se opravdu nevejde, ať
// v tabulátoru nepřibývají zastávky navíc. Role tabulky zůstává.

function initScrollableTables() {
    const tables = document.querySelectorAll('.ln-prose table');
    if (!tables.length) return;

    const update = () => tables.forEach((t) => {
        if (t.scrollWidth > t.clientWidth + 1) {
            if (!t.hasAttribute('tabindex')) t.setAttribute('tabindex', '0');
        } else if (t.getAttribute('tabindex') === '0') {
            t.removeAttribute('tabindex');
        }
    });
    update();
    window.addEventListener('resize', update, { passive: true });
}

// ── Posuvník referencí ───────────────────────────────────────
//
// Swiper je samostatný bundl — načte se jen na stránce s blokem
// referencí. Verzi (kvůli cache) přebírá od app.min.js: oba bundly
// vznikají v jednom kroku sestavení.

const APP_VERSION = (() => {
    try { return new URL(document.currentScript.src).searchParams.get('v') || ''; }
    catch { return ''; }
})();

function initRefSliders() {
    if (!document.querySelector('[data-refswiper]')) return;
    const s = document.createElement('script');
    s.src   = '/www/assets/js/dist/refslider.min.js' + (APP_VERSION ? '?v=' + APP_VERSION : '');
    s.async = true;
    document.head.appendChild(s);
}

document.addEventListener('DOMContentLoaded', () => {
    initStickyHeader();
    initHeaderAutoHide();
    initMobileNav();
    initLeadForm();
    initReveal();
    initRefSliders();
    initScrollableTables();
});
